import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_SORT_STATE = Object.freeze({ by: null, order: "asc" });
const SAVE_DEBOUNCE_MS = 500;

function normalizeSortableColumns(sortableColumns = []) {
    if (Array.isArray(sortableColumns)) {
        return sortableColumns.reduce((map, column) => {
            if (column?.columnKey && column?.sortBy) map.set(column.columnKey, column.sortBy);
            return map;
        }, new Map());
    }

    if (sortableColumns && typeof sortableColumns === "object") {
        return Object.entries(sortableColumns).reduce((map, [columnKey, sortBy]) => {
            if (columnKey && sortBy) map.set(columnKey, sortBy);
            return map;
        }, new Map());
    }

    return new Map();
}

function normalizeSortState(state, sortableColumnMap) {
    if (!state || typeof state !== "object" || !state.by) return DEFAULT_SORT_STATE;
    const sortableValues = new Set(sortableColumnMap.values());
    if (!sortableValues.has(state.by)) return DEFAULT_SORT_STATE;

    return {
        by: state.by,
        order: state.order === "desc" ? "desc" : "asc",
    };
}

function areSortStatesEqual(left, right) {
    return left.by === right.by && left.order === right.order;
}

function resolveNextSortState(currentState, columnKey, sortableColumnMap) {
    const sortBy = sortableColumnMap.get(columnKey);
    if (!sortBy) return currentState;
    if (currentState.by !== sortBy) return { by: sortBy, order: "asc" };
    if (currentState.order === "asc") return { by: sortBy, order: "desc" };
    return DEFAULT_SORT_STATE;
}

export function useGridSort({ sortableColumns = [], initialSort, persistence, onSortChange } = {}) {
    const sortableColumnMap = useMemo(() => normalizeSortableColumns(sortableColumns), [sortableColumns]);
    const [state, setState] = useState(() => normalizeSortState(initialSort, sortableColumnMap));
    const [isLoaded, setIsLoaded] = useState(() => !persistence);
    const sortableColumnMapRef = useRef(sortableColumnMap);
    const stateRef = useRef(state);
    const onSortChangeRef = useRef(onSortChange);
    const saveVersionRef = useRef(0);
    const saveRequestedRef = useRef(false);

    sortableColumnMapRef.current = sortableColumnMap;
    stateRef.current = state;
    onSortChangeRef.current = onSortChange;

    useEffect(() => {
        setState((currentState) => {
            const nextState = normalizeSortState(currentState, sortableColumnMap);
            return areSortStatesEqual(currentState, nextState) ? currentState : nextState;
        });
    }, [sortableColumnMap]);

    useEffect(() => {
        if (!persistence) {
            setIsLoaded(true);
            return undefined;
        }

        let cancelled = false;
        setIsLoaded(false);

        async function loadSortState() {
            try {
                const persistedSortState = await persistence.load();
                if (cancelled) return;
                const nextState = normalizeSortState(persistedSortState, sortableColumnMapRef.current);
                setState((currentState) => (areSortStatesEqual(currentState, nextState) ? currentState : nextState));
                if (!areSortStatesEqual(stateRef.current, nextState)) onSortChangeRef.current?.(nextState);
                stateRef.current = nextState;
            } catch (error) {
                console.error("加载 GridCore 排序配置失败", error);
            } finally {
                if (!cancelled) setIsLoaded(true);
            }
        }

        loadSortState();
        return () => {
            cancelled = true;
        };
    }, [persistence]);

    useEffect(() => {
        if (!persistence || !isLoaded || !saveRequestedRef.current) return undefined;

        const saveVersion = saveVersionRef.current;
        const stateSnapshot = state;
        const saveTimer = window.setTimeout(() => {
            persistence
                .save(stateSnapshot)
                .catch((error) => {
                    console.error("保存 GridCore 排序配置失败", error);
                })
                .finally(() => {
                    if (saveVersionRef.current === saveVersion) saveRequestedRef.current = false;
                });
        }, SAVE_DEBOUNCE_MS);

        return () => window.clearTimeout(saveTimer);
    }, [isLoaded, persistence, state]);

    const isColumnSortable = useCallback((columnKey) => Boolean(sortableColumnMapRef.current.get(columnKey)), []);

    const getColumnSortOrder = useCallback((columnKey) => {
        const sortBy = sortableColumnMapRef.current.get(columnKey);
        if (!sortBy || stateRef.current.by !== sortBy) return null;
        return stateRef.current.order;
    }, []);

    const toggleColumnSort = useCallback((columnKey) => {
        const nextState = resolveNextSortState(stateRef.current, columnKey, sortableColumnMapRef.current);
        if (areSortStatesEqual(stateRef.current, nextState)) return;

        setState(nextState);
        stateRef.current = nextState;
        saveRequestedRef.current = true;
        saveVersionRef.current += 1;
        onSortChangeRef.current?.(nextState);
    }, []);

    const resetSort = useCallback(() => {
        if (areSortStatesEqual(stateRef.current, DEFAULT_SORT_STATE)) return;

        setState(DEFAULT_SORT_STATE);
        stateRef.current = DEFAULT_SORT_STATE;
        saveRequestedRef.current = true;
        saveVersionRef.current += 1;
        onSortChangeRef.current?.(DEFAULT_SORT_STATE);
    }, []);

    return useMemo(
        () => ({
            state,
            sortableColumns,
            isLoaded,
            isColumnSortable,
            getColumnSortOrder,
            toggleColumnSort,
            resetSort,
        }),
        [getColumnSortOrder, isColumnSortable, isLoaded, resetSort, sortableColumns, state, toggleColumnSort],
    );
}

export default useGridSort;
